import { useEffect, useRef, useState } from "react";
import { useDraft } from "@/hooks/use-draft";
import { ComposerTextarea, SEND_BUTTON_CLASS, SendHint } from "@/components/composer-textarea";
import type { MentionCandidate } from "@/components/mention-autocomplete";
import { ArrowUp, CornerDownRight, Loader2, Mic, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/tooltip";
import { StopRoundButton, type useStopAgent } from "@/components/stop-agent-button";
import { Elapsed, LevelMeter } from "@/components/voice-controls";
import { ImageGroup } from "@/components/image-viewer";
import { api, ApiError } from "@/lib/api";
import { humanBytes } from "@/lib/files";
import { FileIcon } from "@/components/file-chip";
import { previewText } from "@/lib/preview-text";
import type { AgentMessage, AgentWithContext } from "@/lib/types";
import { cn } from "@/lib/utils";
import { unlockAudio } from "@/lib/voice/audio";
import { useAutoSend, useVoiceSupport } from "@/lib/voice/settings";
import { startDictation, type Dictation } from "@/lib/voice/stt";

/** Per message. Any type, and the size cap is the server's (`AGENT_FILE_MAX_MB`), checked before a byte is sent. */
const MAX_FILES = 10;
/** Held longer than this, the mic is push-to-talk: letting go stops it. */
const HOLD_MS = 450;

/** What was typed, then what was said, with one space between. */
const joinText = (before: string, said: string) => (before.trim() && said ? `${before.replace(/\s+$/, "")} ${said}` : before + said);

interface Pending {
  key: string;
  file: File;
  /** An object URL for an image's thumbnail. */
  preview: string | null;
  /** Fraction uploaded while sending; null before. */
  progress: number | null;
  /** Set once the file is on the server, so a retry after a failed send does not upload it again. */
  uploadId?: string;
}

/**
 * The box under an agent's conversation. Enter is a newline; Send or Ctrl/⌘+Enter
 * sends (see `ComposerTextarea`). Files come in by the paperclip, by dropping them
 * anywhere on the box, or by pasting (a screenshot off the clipboard included).
 * Any type goes: each file is streamed up on its own (`api.uploadAgentFile`, with
 * progress on its chip) before the message names them. Text and source files are
 * handed to the agent inline, images and PDFs saved for it to open with Read, and
 * anything else — a zip, an APK, a spreadsheet — saved for its own tools.
 *
 * The mic dictates into the same box (`lib/voice/stt.ts`): tap to start and tap to
 * stop, or hold to talk. What was said lands in the box to be read and edited, and
 * goes out through the same Send as typing — unless "Auto-send when I stop talking"
 * is on — so a dictated message reaches a busy agent's run exactly as a typed one does.
 */
export function AgentComposer({
  agent,
  replyTo,
  onClearReply,
  onSent,
  mentions,
  queued,
  stopControl,
}: {
  agent: AgentWithContext;
  /** Agents an `@` here can tag; a tagged one is sent a copy in its own chat. */
  mentions: MentionCandidate[];
  /** Turns waiting behind the running one, for Stop's "clear the queue" choice. */
  queued: number;
  /** Shared with the header's ⋯ menu, so "Stopping…" shows in both. */
  stopControl: ReturnType<typeof useStopAgent>;
  /** A question (or suggestion) this message answers, sent with it so the agent knows. */
  replyTo: AgentMessage | null;
  onClearReply: () => void;
  onSent: (message: AgentMessage) => void;
}) {
  const [text, setText] = useDraft(`agent:${agent.id}`);
  const [files, setFiles] = useState<Pending[]>([]);
  const [sending, setSending] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);

  const voice = useVoiceSupport();
  const [autoSend] = useAutoSend();
  const [recording, setRecording] = useState<Dictation | null>(null);
  const [starting, setStarting] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  /** When the mic went down while it may still be a hold; null once it is a tap; -1 released mid-start. */
  const pressedAt = useRef<number | null>(null);
  const dictation = useRef<Dictation | null>(null);
  /** What was in the box before speaking; the words said go after it. */
  const before = useRef("");

  useEffect(() => () => files.forEach((pending) => pending.preview && URL.revokeObjectURL(pending.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = (incoming: File[]) => {
    const refused: string[] = [];
    const accepted: Pending[] = [];
    for (const file of incoming) {
      if (file.size === 0) refused.push(`${file.name} is empty`);
      else {
        accepted.push({
          key: `${file.name}-${file.size}-${Math.random()}`,
          file,
          preview: /^image\/(png|jpeg|gif|webp)$/.test(file.type) ? URL.createObjectURL(file) : null,
          progress: null,
        });
      }
    }
    setFiles((current) => {
      const next = [...current, ...accepted];
      if (next.length > MAX_FILES) refused.push(`at most ${MAX_FILES} files per message`);
      return next.slice(0, MAX_FILES);
    });
    setNotice(refused.length ? refused.join(" · ") : null);
  };

  const remove = (key: string) =>
    setFiles((current) => {
      const gone = current.find((pending) => pending.key === key);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return current.filter((pending) => pending.key !== key);
    });

  const send = async (override?: string) => {
    const body = (override ?? text).trim();
    if ((!body && files.length === 0) || sending) return;
    setSending(true);
    setNotice(null);
    try {
      // One at a time: the chips fill in order, and a dropped connection costs one file, not all.
      const uploads: string[] = [];
      for (const pending of files) {
        if (!pending.uploadId) {
          const setProgress = (progress: number | null, uploadId?: string) =>
            setFiles((current) => current.map((other) => (other.key === pending.key ? { ...other, progress, uploadId: uploadId ?? other.uploadId } : other)));
          setProgress(0);
          try {
            const named = pending.file.name ? pending.file : new File([pending.file], "pasted.png", { type: pending.file.type });
            const uploaded = await api.uploadAgentFile(agent.id, named, (fraction) => setProgress(fraction));
            pending.uploadId = uploaded.uploadId;
            setProgress(1, uploaded.uploadId);
          } catch (cause) {
            setProgress(null);
            throw cause;
          }
        }
        uploads.push(pending.uploadId!);
      }
      let message: AgentMessage;
      let rejected: Array<{ filename: string; reason: string }>;
      let forwardedTo: Array<{ handle: string }> = [];
      if (replyTo) {
        const result = await api.respondToAgent(agent.id, replyTo.id, {
          action: replyTo.kind === "suggestion" ? "accept" : "answer",
          body,
          uploads,
        });
        if (!result.reply) throw new Error("no reply was queued");
        ({ message, rejected } = result.reply);
        onClearReply();
      } else {
        // No chip here means the person cleared it (one appears for every open question),
        // so this is not an answer — the server would otherwise take it as one.
        ({ message, rejected, forwardedTo } = await api.postAgentMessage(agent.id, { body, uploads, answers: false }));
      }
      setText("");
      files.forEach((pending) => pending.preview && URL.revokeObjectURL(pending.preview));
      setFiles([]);
      onSent(message);
      const notes = [
        forwardedTo.length > 0 ? `Also sent to ${forwardedTo.map((other) => `@${other.handle}`).join(", ")}` : "",
        rejected.length > 0 ? `Sent without: ${rejected.map((file) => file.reason).join(" · ")}` : "",
      ].filter(Boolean);
      if (notes.length > 0) setNotice(notes.join(" · "));
    } catch (cause) {
      setNotice(cause instanceof ApiError ? cause.message : "Could not send that");
    } finally {
      setSending(false);
      box.current?.focus();
    }
  };

  /* ---------------------------------------------------------------- voice in */

  // A dictation can end by itself, from a callback made renders ago: read these fresh.
  const latest = useRef({ send, autoSend });
  latest.current = { send, autoSend };

  const finishDictation = async () => {
    const current = dictation.current;
    if (!current) return;
    dictation.current = null;
    pressedAt.current = null;
    setRecording(null);
    setTranscribing(true);
    try {
      const said = await current.stop();
      const next = joinText(before.current, said);
      setText(next);
      if (!said) {
        setNotice("Didn't catch that. Try again, a little closer to the mic.");
      } else if (latest.current.autoSend) {
        void latest.current.send(next);
      } else {
        requestAnimationFrame(() => {
          const el = box.current;
          if (!el) return;
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        });
      }
    } catch (cause) {
      setText(before.current);
      setNotice(cause instanceof Error ? cause.message : "Could not transcribe that");
    } finally {
      setTranscribing(false);
    }
  };

  const beginDictation = async () => {
    if (dictation.current || starting || transcribing) return;
    unlockAudio();
    setNotice(null);
    setStarting(true);
    before.current = text;
    try {
      const started = await startDictation({
        agentId: agent.id,
        onInterim: (said) => setText(joinText(before.current, said)),
      });
      dictation.current = started;
      setRecording(started);
      // The length cap, or the browser's recogniser ending by itself: take what was said.
      void started.ended.then((end) => {
        if (dictation.current === started && end !== "manual" && end !== "cancelled") void finishDictation();
      });
      // Let go of a hold before the mic was even up.
      if (pressedAt.current === -1) void finishDictation();
    } catch (cause) {
      pressedAt.current = null;
      setNotice(cause instanceof Error ? cause.message : "Could not start voice input");
    } finally {
      setStarting(false);
    }
  };

  const cancelDictation = () => {
    dictation.current?.cancel();
    dictation.current = null;
    pressedAt.current = null;
    setRecording(null);
    setText(before.current);
  };

  useEffect(() => () => dictation.current?.cancel(), []);

  /** Tap to start, tap to stop; or hold to talk and let go to stop. */
  const micDown = () => {
    if (dictation.current) {
      if (pressedAt.current === null) void finishDictation();
      return;
    }
    pressedAt.current = Date.now();
    void beginDictation();
  };
  const micUp = () => {
    const at = pressedAt.current;
    if (at === null || at === -1) return;
    if (Date.now() - at >= HOLD_MS) {
      if (dictation.current) void finishDictation();
      else pressedAt.current = -1;
    } else {
      pressedAt.current = null; // A tap: keep listening until the next one.
    }
  };
  const listening = Boolean(recording) || starting;

  return (
    <div className="border-t border-border/70 bg-background/80 px-4 pb-[max(0.75rem,var(--safe-bottom,env(safe-area-inset-bottom)))] pt-2.5 win-md:px-6">
      <div className="mx-auto max-w-3xl">
        <div
          onDragOver={(event) => {
            if (!event.dataTransfer.types.includes("Files")) return;
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            if (!event.dataTransfer.files.length) return;
            event.preventDefault();
            setDragging(false);
            add([...event.dataTransfer.files]);
          }}
          className={cn(
            "rounded-xl border bg-card shadow-sm transition-colors focus-within:border-ring/50",
            dragging ? "border-primary border-dashed bg-primary/5" : "border-border",
          )}
        >
          {replyTo ? (
            <div className="flex items-start gap-2 border-b border-border/60 px-3 py-2 text-[12px]">
              <CornerDownRight className="mt-0.5 size-3.5 shrink-0" style={{ color: replyTo.kind === "question" ? "var(--waiting)" : "var(--kind-active)" }} />
              <p className="line-clamp-2 min-w-0 flex-1">
                <span className="font-medium">
                  {replyTo.kind === "suggestion" ? "Accepting" : "Answering"} {agent.name}'s {replyTo.kind}:
                </span>{" "}
                <span className="text-muted-foreground">{previewText(replyTo.body)}</span>
              </p>
              <button aria-label="Send as an ordinary message" onClick={onClearReply} className="text-muted-foreground hover:text-foreground">
                <X className="size-3.5" />
              </button>
            </div>
          ) : null}
          {recording || transcribing ? (
            <div className="flex items-center gap-2.5 border-b border-border/60 px-3 py-2 text-[12px]" role="status" aria-live="polite">
              {recording ? (
                <>
                  <span className="size-2 shrink-0 animate-pulse rounded-full" style={{ backgroundColor: "var(--destructive)" }} />
                  <Elapsed startedAt={recording.startedAt} className="font-medium" />
                  <LevelMeter level={recording.level} bars={28} className="min-w-0 flex-1 overflow-hidden text-primary" />
                  <span className="hidden shrink-0 text-muted-foreground win-sm:inline">
                    {autoSend ? "tap the mic to send" : "tap the mic to stop"}
                  </span>
                  <button aria-label="Cancel recording" onClick={cancelDictation} className="text-muted-foreground hover:text-foreground">
                    <X className="size-3.5" />
                  </button>
                </>
              ) : (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" /> Transcribing…
                </span>
              )}
            </div>
          ) : null}
          {files.length > 0 ? (
            <ImageGroup
              images={files.flatMap((pending) => (pending.preview ? [{ src: pending.preview, filename: pending.file.name || "pasted image" }] : []))}
              className="flex flex-wrap gap-2 px-3 pt-2.5"
            >
              {(openImage) => files.map((pending) => (
                <span
                  key={pending.key}
                  className="group relative flex max-w-52 items-center gap-2 rounded-lg border border-border/80 bg-muted/50 p-1 pr-2 text-[12px]"
                >
                  {pending.preview ? (
                    <button
                      type="button"
                      onClick={() => openImage(files.filter((other) => other.preview).indexOf(pending))}
                      aria-label={`View ${pending.file.name || "pasted image"}`}
                      className="shrink-0 cursor-zoom-in"
                    >
                      <img src={pending.preview} alt="" className="size-9 shrink-0 rounded-md object-cover" />
                    </button>
                  ) : (
                    <span className="grid size-9 shrink-0 place-items-center rounded-md bg-background text-muted-foreground">
                      <FileIcon filename={pending.file.name} className="size-4" />
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{pending.file.name || "pasted image"}</span>
                    <span className="block text-[10.5px] text-muted-foreground tabular-nums">
                      {pending.progress !== null && pending.progress < 1
                        ? `${Math.round(pending.progress * 100)}% of ${humanBytes(pending.file.size)}`
                        : humanBytes(pending.file.size)}
                    </span>
                  </span>
                  {pending.progress !== null ? (
                    <span
                      role="progressbar"
                      aria-label={`Uploading ${pending.file.name}`}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(pending.progress * 100)}
                      className="absolute inset-x-1 bottom-0.5 h-0.5 overflow-hidden rounded-full bg-border"
                    >
                      <span className="block h-full bg-primary transition-[width]" style={{ width: `${pending.progress * 100}%` }} />
                    </span>
                  ) : null}
                  <button
                    aria-label={`Remove ${pending.file.name}`}
                    onClick={() => remove(pending.key)}
                    disabled={sending}
                    className="absolute -right-1.5 -top-1.5 grid size-5 place-items-center rounded-full border border-border bg-background text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </ImageGroup>
          ) : null}
          <div className="flex items-end gap-1.5 px-2 py-2">
            <Hint label="Attach files — any type">
              <Button variant="ghost" size="icon-sm" aria-label="Attach files" onClick={() => picker.current?.click()}>
                <Paperclip />
              </Button>
            </Hint>
            <input
              ref={picker}
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                add([...(event.target.files ?? [])]);
                event.target.value = "";
              }}
            />
            <ComposerTextarea
              ref={box}
              onSend={() => void send()}
              mentions={mentions}
              wrapperClassName="min-w-0 flex-1"
              rows={1}
              value={text}
              readOnly={transcribing}
              placeholder={recording ? "Listening…" : replyTo ? "Your answer…" : `Message ${agent.name}…`}
              onChange={(event) => setText(event.target.value)}
              onPaste={(event) => {
                const pasted = [...event.clipboardData.files];
                if (pasted.length > 0) {
                  event.preventDefault();
                  add(pasted);
                }
              }}
              className="min-h-6 py-1 text-[14px] leading-relaxed"
            />
            {voice.canListen ? (
              <Hint label={listening ? "Stop and transcribe" : "Dictate: tap to start and stop, or hold to talk"}>
                <Button
                  variant={listening ? "destructive" : "ghost"}
                  size="icon-sm"
                  className={cn("touch-none select-none rounded-full", listening && "ring-4 ring-destructive/25")}
                  aria-label={listening ? "Stop dictating" : "Dictate"}
                  aria-pressed={listening}
                  disabled={transcribing || sending}
                  onPointerDown={(event) => {
                    if (event.button !== 0) return;
                    event.preventDefault();
                    micDown();
                  }}
                  onPointerUp={micUp}
                  onPointerCancel={micUp}
                  onContextMenu={(event) => event.preventDefault()}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    if (dictation.current) void finishDictation();
                    else void beginDictation();
                  }}
                >
                  {transcribing ? <Loader2 className="animate-spin" /> : <Mic />}
                </Button>
              </Hint>
            ) : null}
            {agent.status === "working" ? <StopRoundButton control={stopControl} agentName={agent.name} queued={queued} /> : null}
            <Button
              size="icon-sm"
              className={SEND_BUTTON_CLASS}
              aria-label={agent.status === "stopped" ? "Send (queued until resumed)" : "Send"}
              loading={sending}
              disabled={(!text.trim() && files.length === 0) || listening || transcribing}
              onClick={() => void send()}
            >
              <ArrowUp />
            </Button>
          </div>
        </div>
        <div className="mt-1 flex items-center gap-3 px-1 text-[10.5px] text-muted-foreground">
          {notice ? (
            <span className="min-w-0 flex-1 text-destructive">{notice}</span>
          ) : (
            <span className="flex min-w-0 flex-1 items-center gap-1.5">
              <SendHint />
              <span className="hidden win-sm:inline">· drop or paste files</span>
              {voice.canListen ? <span className="hidden win-sm:inline">· hold the mic to talk</span> : null}
            </span>
          )}
          {agent.status === "working" ? (
            <span className="ml-auto shrink-0">
              {stopControl.stopping ? "stopping…" : agent.liveInputOpen ? "delivered to the current run" : "queues behind the current run"}
            </span>
          ) : agent.status === "stopped" ? (
            <span className="ml-auto shrink-0">queued until resumed</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
